// TopWallet — Aggregated articles hub
// Route: /topwallet/articulos · Topical authority hub for SEO
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Search, Clock, BookOpen } from "lucide-react";
import { ASSETS } from "../assets";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { useLanguage } from "../i18n/LanguageContext";
import { getTopWalletI18n } from "../i18n/topwallet";
import { ARTICULOS_TW, getArticuloLocalizado } from "../data/topwalletArticulos";
import { SITE_URL, OG_IMAGE } from "../i18n/seo_routes";

const HREF_MAP = { es: "es-ES", en: "en-US", sk: "sk-SK", de: "de-DE", "zh-CN": "zh-CN" };

const NIVEL_STYLES = {
  basico: { border: "rgba(0,230,118,.5)", bg: "rgba(0,230,118,.12)", text: "#00e676" },
  intermedio: { border: "rgba(245,166,35,.5)", bg: "rgba(245,166,35,.12)", text: "#f5a623" },
  avanzado: { border: "rgba(255,51,85,.5)", bg: "rgba(255,51,85,.12)", text: "#ff3355" },
};

const setMeta = (sel, attr, value) => {
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement("meta");
    const m = sel.match(/\[([^=]+)="([^"]+)"\]/);
    if (m) el.setAttribute(m[1], m[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const setLink = (rel, href, attrs = {}) => {
  let s = `link[rel="${rel}"]`;
  Object.entries(attrs).forEach(([k, v]) => (s += `[${k}="${v}"]`));
  let el = document.head.querySelector(s);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const setJsonLd = (id, data) => {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

const TopWalletArticulosPage = () => {
  const { lang } = useLanguage();
  const ui = useMemo(() => getTopWalletI18n(lang), [lang]);
  const t = ui.hubArticulos;

  const [secFilter, setSecFilter] = useState("all");
  const [nivFilter, setNivFilter] = useState("all");
  const [query, setQuery] = useState("");

  // Build localized article list once per lang
  const allArticles = useMemo(
    () =>
      ARTICULOS_TW.map((a) => getArticuloLocalizado(a, lang))
        .sort((a, b) => a.seccion.localeCompare(b.seccion) || a.orden - b.orden),
    [lang]
  );

  // Apply filters + search
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allArticles.filter((a) => {
      if (secFilter !== "all" && a.seccion !== secFilter) return false;
      if (nivFilter !== "all" && a.nivel !== nivFilter) return false;
      if (q) {
        const haystack = `${a.titulo} ${a.resumen}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [allArticles, secFilter, nivFilter, query]);

  // SEO meta + JSON-LD
  useEffect(() => {
    const url = `${SITE_URL}/topwallet/articulos`;
    const hreflang = HREF_MAP[lang] || "es-ES";
    document.title = `${t.titulo} · TopWallet · RE₿EL`;
    document.documentElement.lang = lang;
    setMeta('meta[name="description"]', "content", t.subtitulo);
    setMeta(
      'meta[name="robots"]',
      "content",
      "index, follow, max-snippet:-1, max-image-preview:large"
    );
    setLink("canonical", url);
    setLink("alternate", `${url}?lang=es`, { hreflang: "es-ES" });
    setLink("alternate", `${url}?lang=en`, { hreflang: "en-US" });
    setLink("alternate", `${url}?lang=sk`, { hreflang: "sk-SK" });
    setLink("alternate", url, { hreflang: "x-default" });
    setMeta('meta[property="og:type"]', "content", "website");
    setMeta('meta[property="og:title"]', "content", t.titulo);
    setMeta('meta[property="og:description"]', "content", t.subtitulo);
    setMeta('meta[property="og:url"]', "content", url);
    setMeta('meta[property="og:image"]', "content", OG_IMAGE);
    setMeta('meta[property="og:locale"]', "content", hreflang.replace("-", "_"));
    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");

    const graph = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "CollectionPage",
          "@id": `${url}#collection`,
          name: t.titulo,
          description: t.subtitulo,
          inLanguage: hreflang,
          isPartOf: { "@id": `${SITE_URL}#website` },
          mainEntity: { "@id": `${url}#itemlist` },
        },
        {
          "@type": "ItemList",
          "@id": `${url}#itemlist`,
          numberOfItems: allArticles.length,
          itemListElement: allArticles.map((a, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `${SITE_URL}/topwallet/${a.seccion}/${a.slug}`,
            name: a.titulo,
          })),
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "RE₿EL BTC", item: SITE_URL },
            { "@type": "ListItem", position: 2, name: "TopWallet", item: `${SITE_URL}/#topwallet` },
            { "@type": "ListItem", position: 3, name: t.titulo, item: url },
          ],
        },
      ],
    };
    setJsonLd("rebel-jsonld", graph);
  }, [lang, t, allArticles]);

  const SEC_OPTIONS = [
    { id: "all", label: t.todas },
    ...ui.cards.map((c) => ({ id: c.id, label: c.titulo })),
  ];
  const NIV_OPTIONS = [
    { id: "all", label: t.todos },
    { id: "basico", label: ui.nivelBadge.basico },
    { id: "intermedio", label: ui.nivelBadge.intermedio },
    { id: "avanzado", label: ui.nivelBadge.avanzado },
  ];

  const countLabel = filtered.length === 1 ? t.contadorSingular : t.contador;

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-black text-white">
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(59,130,246,0.22),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_60%,rgba(245,166,35,0.10),transparent_55%)]" />
      </div>

      <header className="relative z-20 border-b border-white/5 bg-black/60 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 md:px-10 md:py-4">
          <Link
            to="/#topwallet"
            className="inline-flex items-center gap-2 text-white/70 transition-colors hover:text-white"
            data-testid="articulos-back"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-xs font-bold uppercase tracking-[0.2em]">{ui.breadcrumbRoot}</span>
          </Link>
          <img src={ASSETS.logos.rebel} alt="REBEL" className="h-8 w-auto md:h-10" />
          <LanguageSwitcher />
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-6xl px-4 py-10 md:px-10 md:py-14">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-10"
        >
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/5 px-3 py-1 backdrop-blur">
            <BookOpen className="h-3.5 w-3.5 text-cyan-300" />
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-cyan-200">
              {t.pretitulo}
            </span>
          </div>
          <h1
            data-testid="articulos-title"
            className="font-display text-3xl font-black leading-tight tracking-tight md:text-5xl"
          >
            {t.titulo}
          </h1>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-white/65 md:text-lg">
            {t.subtitulo}
          </p>
        </motion.div>

        {/* Filtros */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-8 rounded-2xl border border-white/10 bg-white/[0.03] p-4 backdrop-blur-xl md:p-5"
        >
          {/* Search */}
          <div className="relative mb-4">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t.buscar}
              data-testid="articulos-search"
              className="w-full rounded-full border border-white/10 bg-black/30 py-3 pl-11 pr-4 text-sm text-white placeholder:text-white/40 focus:border-cyan-400/40 focus:outline-none"
            />
          </div>

          {/* Section filter */}
          <div className="mb-3">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.22em] text-white/45">
              {t.filtroSeccion}
            </div>
            <div className="flex flex-wrap gap-2">
              {SEC_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setSecFilter(opt.id)}
                  data-testid={`articulos-sec-${opt.id}`}
                  className={`rounded-full border px-3.5 py-1.5 text-xs font-bold transition-all ${
                    secFilter === opt.id
                      ? "border-cyan-400/60 bg-cyan-400/15 text-cyan-200"
                      : "border-white/10 bg-white/[0.03] text-white/55 hover:border-white/25 hover:text-white"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Level filter */}
          <div>
            <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.22em] text-white/45">
              {t.filtroNivel}
            </div>
            <div className="flex flex-wrap gap-2">
              {NIV_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setNivFilter(opt.id)}
                  data-testid={`articulos-niv-${opt.id}`}
                  className={`rounded-full border px-3.5 py-1.5 text-xs font-bold transition-all ${
                    nivFilter === opt.id
                      ? "border-amber-400/60 bg-amber-400/15 text-amber-200"
                      : "border-white/10 bg-white/[0.03] text-white/55 hover:border-white/25 hover:text-white"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Counter */}
        <div className="mb-5 text-xs font-bold uppercase tracking-[0.25em] text-white/45">
          <span data-testid="articulos-count" className="text-cyan-300">
            {filtered.length}
          </span>{" "}
          {countLabel}
        </div>

        {/* Grid */}
        {filtered.length === 0 ? (
          <div
            data-testid="articulos-empty"
            className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center text-white/55"
          >
            {t.sinResultados}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-5">
            {filtered.map((art, i) => {
              const ns = NIVEL_STYLES[art.nivel] || NIVEL_STYLES.basico;
              const sectionLabel = ui.cards.find((c) => c.id === art.seccion)?.titulo || art.seccion;
              return (
                <motion.article
                  key={art.slug}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.45, delay: 0.03 * (i % 9) }}
                  data-testid={`articulos-card-${art.slug}`}
                  className="group relative flex flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl transition-all hover:border-cyan-400/40 hover:shadow-[0_0_30px_rgba(59,130,246,0.18)]"
                >
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <div
                      className="flex h-11 w-11 items-center justify-center rounded-xl text-2xl"
                      style={{
                        background: "rgba(0,212,255,0.08)",
                        border: "1px solid rgba(0,212,255,0.25)",
                      }}
                    >
                      {art.icono}
                    </div>
                    <span
                      className="rounded-full px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.2em]"
                      style={{ border: `1px solid ${ns.border}`, background: ns.bg, color: ns.text }}
                    >
                      {ui.nivelBadge[art.nivel]}
                    </span>
                  </div>
                  <div className="mb-2 text-[9px] font-bold uppercase tracking-[0.2em] text-cyan-300/70">
                    {sectionLabel}
                  </div>
                  <h3 className="text-base font-bold leading-snug text-white md:text-lg">
                    {art.titulo}
                  </h3>
                  <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-white/60">
                    {art.resumen}
                  </p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-white/45">
                      <Clock className="h-3 w-3" />
                      {art.tiempoLectura}
                    </span>
                    <Link
                      to={`/topwallet/${art.seccion}/${art.slug}`}
                      data-testid={`articulos-cta-${art.slug}`}
                      className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-cyan-300 transition-colors hover:text-cyan-200"
                    >
                      {ui.leerArticulo}
                    </Link>
                  </div>
                </motion.article>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default TopWalletArticulosPage;
